import { IconButton, Tooltip } from '@pay-com/components'
import { Download } from '@pay-com/icons'
import { ConditionalWrapper } from 'components/ConditionalWrapper'
import {
  DocumentUploadStatus,
  useGetDocumentUrlLazyQuery
} from 'generated/graphql'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { twMerge } from 'tailwind-merge'

const getUploadStatusTooltipText = (
  t: TFunction,
  status?: DocumentUploadStatus | null
): string => {
  switch (status) {
    case DocumentUploadStatus.Created:
      return t('tasks.documentStatus.processing')
    case DocumentUploadStatus.Uploaded:
      return t('tasks.documentStatus.reviewing')
    case DocumentUploadStatus.Rejected:
      return t('tasks.documentStatus.rejected')
    default:
      return t('tasks.documentStatus.unavailable')
  }
}

const createClickAndRemoveLink = (url: string, filename: string) => {
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

interface DownloadButtonProps {
  id: string
  uploadStatus?: DocumentUploadStatus | null
  disableButtonStyles?: boolean
}

export const DownloadButton = ({
  id,
  uploadStatus,
  disableButtonStyles = false
}: DownloadButtonProps) => {
  const { t } = useTranslation()
  const [getDownloadUrl, { loading }] = useGetDocumentUrlLazyQuery({
    variables: { input: { id } },
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'network-only',
    onCompleted: newData => {
      createClickAndRemoveLink(newData.documentUrl.url, 'file')
    }
  })

  const download = async () => {
    await getDownloadUrl()
  }

  const isAccepted = uploadStatus === DocumentUploadStatus.Accepted
  const isDisabled = loading || !isAccepted

  return (
    <ConditionalWrapper
      condition={!isAccepted}
      wrapper={Tooltip}
      wrapperProps={{
        tooltipText: getUploadStatusTooltipText(t, uploadStatus)
      }}
    >
      <IconButton
        variant="secondary"
        onClick={download}
        disabled={isDisabled}
        className={twMerge(
          disableButtonStyles &&
            '!border-none !bg-none p-2 !shadow-transparent hover:!bg-transparent'
        )}
        isLoading={loading}
      >
        <Download />
      </IconButton>
    </ConditionalWrapper>
  )
}
